import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export interface MapObstacle {
  mesh: THREE.Mesh;
  body: CANNON.Body;
  position: THREE.Vector3;
  size: { width: number; height: number; depth: number };
}

export interface TerrainFeature {
  type: 'hill' | 'ramp' | 'platform' | 'pit';
  mesh: THREE.Mesh;
  body: CANNON.Body;
  position: THREE.Vector3;
  size: { width: number; height: number; depth: number };
}

export interface MapData {
  name: string;
  size: number;
  obstacles: MapObstacle[];
  spawnPoints: THREE.Vector3[];
  enemySpawnPoints: THREE.Vector3[];
  terrainFeatures?: TerrainFeature[];
}

export class MapGenerator {
  private scene: THREE.Scene;
  private physicsWorld: CANNON.World;

  constructor(scene: THREE.Scene, physicsWorld: CANNON.World) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
  }

  // 生成城市地图
  generateCityMap(): MapData {
    const mapSize = 300; // 增大为2倍
    const obstacles: MapObstacle[] = [];

    // 创建建筑物网格 - 扩大范围和数量
    const buildingPositions = [
      // 远左侧建筑群
      { x: -100, z: -100, w: 15, h: 20, d: 15 },
      { x: -100, z: -60, w: 12, h: 15, d: 12 },
      { x: -100, z: -20, w: 18, h: 25, d: 18 },
      { x: -100, z: 20, w: 14, h: 18, d: 14 },
      { x: -100, z: 60, w: 16, h: 22, d: 16 },
      { x: -100, z: 100, w: 13, h: 16, d: 13 },

      // 中左侧建筑群
      { x: -50, z: -100, w: 17, h: 24, d: 17 },
      { x: -50, z: -50, w: 15, h: 20, d: 15 },
      { x: -50, z: 0, w: 12, h: 15, d: 12 },
      { x: -50, z: 50, w: 18, h: 25, d: 18 },
      { x: -50, z: 100, w: 14, h: 18, d: 14 },

      // 中央建筑
      { x: 0, z: -80, w: 20, h: 30, d: 20 },
      { x: 0, z: -40, w: 16, h: 25, d: 16 },
      { x: 0, z: 40, w: 20, h: 30, d: 20 },
      { x: 0, z: 80, w: 18, h: 28, d: 18 },

      // 中右侧建筑群
      { x: 50, z: -100, w: 15, h: 19, d: 15 },
      { x: 50, z: -50, w: 16, h: 22, d: 16 },
      { x: 50, z: 0, w: 13, h: 16, d: 13 },
      { x: 50, z: 50, w: 17, h: 24, d: 17 },
      { x: 50, z: 100, w: 15, h: 19, d: 15 },

      // 远右侧建筑群
      { x: 100, z: -100, w: 14, h: 18, d: 14 },
      { x: 100, z: -60, w: 16, h: 22, d: 16 },
      { x: 100, z: -20, w: 13, h: 16, d: 13 },
      { x: 100, z: 20, w: 17, h: 24, d: 17 },
      { x: 100, z: 60, w: 15, h: 19, d: 15 },
      { x: 100, z: 100, w: 12, h: 15, d: 12 },

      // 小型建筑/掩体
      { x: -25, z: -25, w: 8, h: 10, d: 8 },
      { x: -25, z: 25, w: 8, h: 10, d: 8 },
      { x: 25, z: -25, w: 8, h: 10, d: 8 },
      { x: 25, z: 25, w: 8, h: 10, d: 8 },
      { x: 0, z: -15, w: 6, h: 8, d: 6 },
      { x: 0, z: 15, w: 6, h: 8, d: 6 },
    ];

    buildingPositions.forEach(pos => {
      const obstacle = this.createBuilding(pos.x, pos.z, pos.w, pos.h, pos.d);
      obstacles.push(obstacle);
    });

    // 添加更多箱子/障碍物 - 增加数量和分布范围
    for (let i = 0; i < 50; i++) {
      const angle = (i / 50) * Math.PI * 2;
      const radius = 40 + Math.random() * 90; // 扩大分布半径
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      const obstacle = this.createCrate(x, z);
      obstacles.push(obstacle);
    }

    return {
      name: '城市战场',
      size: mapSize,
      obstacles,
      spawnPoints: [new THREE.Vector3(0, 0, 0)],
      enemySpawnPoints: this.generateCircularSpawnPoints(120, 12) // 增加敌人数量和半径
    };
  }

  // 生成沙漠地图
  generateDesertMap(): MapData {
    const mapSize = 400; // 增大为2倍
    const obstacles: MapObstacle[] = [];

    // 创建更多岩石群 - 增加数量和分布范围
    for (let i = 0; i < 70; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 50 + Math.random() * 150; // 扩大分布半径
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      const obstacle = this.createRock(x, z);
      obstacles.push(obstacle);
    }

    // 创建更多沙丘 - 增加数量和分布范围
    for (let i = 0; i < 35; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 60 + Math.random() * 130; // 扩大分布半径
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      const obstacle = this.createSandDune(x, z);
      obstacles.push(obstacle);
    }

    // 添加更多废弃建筑 - 扩大分布范围
    const ruinPositions = [
      { x: -120, z: -120 },
      { x: 120, z: -120 },
      { x: -120, z: 120 },
      { x: 120, z: 120 },
      { x: -150, z: 0 },
      { x: 150, z: 0 },
      { x: 0, z: -150 },
      { x: 0, z: 150 },
      { x: -80, z: -80 },
      { x: 80, z: 80 },
    ];

    ruinPositions.forEach(pos => {
      const obstacle = this.createRuin(pos.x, pos.z);
      obstacles.push(obstacle);
    });

    return {
      name: '沙漠战场',
      size: mapSize,
      obstacles,
      spawnPoints: [new THREE.Vector3(0, 0, 0)],
      enemySpawnPoints: this.generateCircularSpawnPoints(160, 15) // 增加敌人数量和半径
    };
  }

  // 生成迷宫地图
  generateMazeMap(): MapData {
    const mapSize = 250; // 增大为2倍
    const obstacles: MapObstacle[] = [];
    const wallThickness = 2;
    const corridorWidth = 10;

    // 创建迷宫墙壁 - 扩大网格范围
    for (let i = -8; i <= 8; i++) {
      for (let j = -8; j <= 8; j++) {
        if (Math.random() < 0.6 && !(i === 0 && j === 0)) {
          const x = i * (corridorWidth + wallThickness);
          const z = j * (corridorWidth + wallThickness);

          if (Math.random() < 0.5) {
            // 横墙
            const obstacle = this.createWall(x, z, corridorWidth, wallThickness);
            obstacles.push(obstacle);
          } else {
            // 竖墙
            const obstacle = this.createWall(x, z, wallThickness, corridorWidth);
            obstacles.push(obstacle);
          }
        }
      }
    }

    return {
      name: '迷宫战场',
      size: mapSize,
      obstacles,
      spawnPoints: [new THREE.Vector3(0, 0, 0)],
      enemySpawnPoints: this.generateGridSpawnPoints(80, 5, 5) // 增加网格密度和数量
    };
  }

  // 生成工业区地图
  generateIndustrialMap(): MapData {
    const mapSize = 320; // 增大为2倍
    const obstacles: MapObstacle[] = [];

    // 大型仓库 - 增加数量和分布范围
    const warehouses = [
      { x: -100, z: 0, w: 25, h: 15, d: 40 },
      { x: 100, z: 0, w: 25, h: 15, d: 40 },
      { x: 0, z: -100, w: 40, h: 15, d: 25 },
      { x: 0, z: 100, w: 40, h: 15, d: 25 },
      { x: -60, z: -60, w: 20, h: 12, d: 30 },
      { x: 60, z: 60, w: 20, h: 12, d: 30 },
    ];

    warehouses.forEach(wh => {
      const obstacle = this.createWarehouse(wh.x, wh.z, wh.w, wh.h, wh.d);
      obstacles.push(obstacle);
    });

    // 管道 - 增加数量和分布
    for (let i = 0; i < 16; i++) {
      const x = -80 + i * 10;
      const obstacle = this.createPipe(x, 50);
      obstacles.push(obstacle);
    }
    for (let i = 0; i < 16; i++) {
      const x = -80 + i * 10;
      const obstacle = this.createPipe(x, -50);
      obstacles.push(obstacle);
    }

    // 储罐 - 增加数量和分布范围
    const tanks = [
      { x: -80, z: -80 },
      { x: -40, z: -80 },
      { x: 0, z: -80 },
      { x: 40, z: -80 },
      { x: 80, z: -80 },
      { x: -80, z: 80 },
      { x: -40, z: 80 },
      { x: 0, z: 80 },
      { x: 40, z: 80 },
      { x: 80, z: 80 },
      { x: -100, z: 40 },
      { x: -100, z: -40 },
      { x: 100, z: 40 },
      { x: 100, z: -40 },
    ];

    tanks.forEach(tank => {
      const obstacle = this.createStorageTank(tank.x, tank.z);
      obstacles.push(obstacle);
    });

    // 集装箱堆 - 增加数量和分布范围
    for (let i = 0; i < 30; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 60 + Math.random() * 80; // 扩大分布半径
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      const obstacle = this.createContainer(x, z);
      obstacles.push(obstacle);
    }

    return {
      name: '工业区',
      size: mapSize,
      obstacles,
      spawnPoints: [new THREE.Vector3(0, 0, 0)],
      enemySpawnPoints: this.generateCircularSpawnPoints(130, 14) // 增加敌人数量和半径
    };
  }

  // 创建建筑物
  private createBuilding(x: number, z: number, width: number, height: number, depth: number): MapObstacle {
    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      roughness: 0.8,
      metalness: 0.2
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    // 添加窗户细节
    const windowGeometry = new THREE.BoxGeometry(width * 0.9, 0.2, depth * 0.9);
    const windowMaterial = new THREE.MeshStandardMaterial({ color: 0x4488ff, emissive: 0x4488ff, emissiveIntensity: 0.3 });

    for (let i = 1; i < height / 3; i++) {
      const window = new THREE.Mesh(windowGeometry, windowMaterial);
      window.position.set(x, i * 3, z);
      this.scene.add(window);
    }

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建箱子
  private createCrate(x: number, z: number): MapObstacle {
    const size = 2 + Math.random() * 2;
    const geometry = new THREE.BoxGeometry(size, size, size);
    const material = new THREE.MeshStandardMaterial({
      color: 0x8B4513,
      roughness: 0.9
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, size / 2, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(size / 2, size / 2, size / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, size / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, size / 2, z),
      size: { width: size, height: size, depth: size }
    };
  }

  // 创建岩石
  private createRock(x: number, z: number): MapObstacle {
    const width = 3 + Math.random() * 4;
    const height = 2 + Math.random() * 3;
    const depth = 3 + Math.random() * 4;

    const geometry = new THREE.DodecahedronGeometry(Math.max(width, height, depth) / 2, 0);
    const material = new THREE.MeshStandardMaterial({
      color: 0x8B7355,
      roughness: 1,
      flatShading: true
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.rotation.y = Math.random() * Math.PI * 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建沙丘
  private createSandDune(x: number, z: number): MapObstacle {
    const width = 8 + Math.random() * 6;
    const height = 1 + Math.random() * 2;
    const depth = 8 + Math.random() * 6;

    const geometry = new THREE.CylinderGeometry(width / 2, width, height, 8);
    const material = new THREE.MeshStandardMaterial({
      color: 0xC2B280,
      roughness: 0.95
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Cylinder(width / 2, width, height, 8);
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建废墟
  private createRuin(x: number, z: number): MapObstacle {
    const width = 12;
    const height = 8;
    const depth = 12;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x696969,
      roughness: 0.9
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.rotation.y = Math.random() * 0.3 - 0.15;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建墙
  private createWall(x: number, z: number, width: number, depth: number): MapObstacle {
    const height = 4;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.8
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建仓库
  private createWarehouse(x: number, z: number, width: number, height: number, depth: number): MapObstacle {
    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x555555,
      roughness: 0.7,
      metalness: 0.3
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 创建管道
  private createPipe(x: number, z: number): MapObstacle {
    const radius = 1.5;
    const length = 10;

    const geometry = new THREE.CylinderGeometry(radius, radius, length, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.5,
      metalness: 0.7
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, radius, z);
    mesh.rotation.z = Math.PI / 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Cylinder(radius, radius, length, 16);
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, radius, z)
    });
    const quat = new CANNON.Quaternion();
    quat.setFromAxisAngle(new CANNON.Vec3(0, 0, 1), Math.PI / 2);
    body.quaternion.copy(quat);
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, radius, z),
      size: { width: length, height: radius * 2, depth: radius * 2 }
    };
  }

  // 创建储罐
  private createStorageTank(x: number, z: number): MapObstacle {
    const radius = 5;
    const height = 12;

    const geometry = new THREE.CylinderGeometry(radius, radius, height, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0xCCCCCC,
      roughness: 0.3,
      metalness: 0.8
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Cylinder(radius, radius, height, 16);
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width: radius * 2, height, depth: radius * 2 }
    };
  }

  // 创建集装箱
  private createContainer(x: number, z: number): MapObstacle {
    const width = 6;
    const height = 3;
    const depth = 3;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const colors = [0xff6b6b, 0x4ecdc4, 0x45b7d1, 0xf7b731, 0x5f27cd];
    const material = new THREE.MeshStandardMaterial({
      color: colors[Math.floor(Math.random() * colors.length)],
      roughness: 0.6,
      metalness: 0.4
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.rotation.y = Math.random() * Math.PI * 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'obstacle' };
    this.physicsWorld.addBody(body);

    return {
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth }
    };
  }

  // 生成圆形出生点
  private generateCircularSpawnPoints(radius: number, count: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      points.push(new THREE.Vector3(x, 0, z));
    }
    return points;
  }

  // 生成网格出生点
  private generateGridSpawnPoints(spacing: number, rows: number, cols: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const x = (j - cols / 2) * spacing;
        const z = (i - rows / 2) * spacing;
        points.push(new THREE.Vector3(x, 0, z));
      }
    }
    return points;
  }

  // 创建山丘地形
  private createHill(x: number, z: number, radius: number, height: number): TerrainFeature {
    const geometry = new THREE.CylinderGeometry(radius * 0.2, radius, height, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0x5a7832,
      roughness: 0.9
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Cylinder(radius * 0.2, radius, height, 16);
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });
    body.userData = { type: 'terrain', terrainType: 'hill' };
    this.physicsWorld.addBody(body);

    return {
      type: 'hill',
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width: radius * 2, height, depth: radius * 2 }
    };
  }

  // 创建斜坡
  private createRamp(x: number, z: number, width: number, length: number, height: number, rotation: number): TerrainFeature {
    const geometry = new THREE.BoxGeometry(width, height, length);
    const material = new THREE.MeshStandardMaterial({
      color: 0x7a8a5f,
      roughness: 0.85
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height / 2, z);
    mesh.rotation.y = rotation;
    mesh.rotation.x = Math.atan2(height, length);
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, length / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height / 2, z)
    });

    const quat = new CANNON.Quaternion();
    quat.setFromEuler(Math.atan2(height, length), rotation, 0);
    body.quaternion.copy(quat);
    body.userData = { type: 'terrain', terrainType: 'ramp' };
    this.physicsWorld.addBody(body);

    return {
      type: 'ramp',
      mesh,
      body,
      position: new THREE.Vector3(x, height / 2, z),
      size: { width, height, depth: length }
    };
  }

  // 创建平台
  private createPlatform(x: number, z: number, width: number, depth: number, height: number): TerrainFeature {
    const geometry = new THREE.BoxGeometry(width, 1, depth);
    const material = new THREE.MeshStandardMaterial({
      color: 0x8b8680,
      roughness: 0.7,
      metalness: 0.3
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, height, z);
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    this.scene.add(mesh);

    // 添加支柱
    const pillarGeometry = new THREE.CylinderGeometry(0.5, 0.5, height, 8);
    const pillarMaterial = new THREE.MeshStandardMaterial({
      color: 0x6b6660,
      roughness: 0.8
    });

    const pillarPositions = [
      { x: width / 3, z: depth / 3 },
      { x: -width / 3, z: depth / 3 },
      { x: width / 3, z: -depth / 3 },
      { x: -width / 3, z: -depth / 3 }
    ];

    pillarPositions.forEach(pos => {
      const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
      pillar.position.set(x + pos.x, height / 2, z + pos.z);
      pillar.castShadow = true;
      this.scene.add(pillar);
    });

    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, 0.5, depth / 2));
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, height, z)
    });
    body.userData = { type: 'terrain', terrainType: 'platform' };
    this.physicsWorld.addBody(body);

    return {
      type: 'platform',
      mesh,
      body,
      position: new THREE.Vector3(x, height, z),
      size: { width, height: 1, depth }
    };
  }

  // 创建凹坑
  private createPit(x: number, z: number, radius: number, depth: number): TerrainFeature {
    const geometry = new THREE.CylinderGeometry(radius, radius * 1.2, depth, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,
      roughness: 1
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, -depth / 2, z);
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    const shape = new CANNON.Cylinder(radius, radius * 1.2, depth, 16);
    const body = new CANNON.Body({
      mass: 0,
      shape: shape,
      position: new CANNON.Vec3(x, -depth / 2, z)
    });
    body.userData = { type: 'terrain', terrainType: 'pit' };
    this.physicsWorld.addBody(body);

    return {
      type: 'pit',
      mesh,
      body,
      position: new THREE.Vector3(x, -depth / 2, z),
      size: { width: radius * 2, height: depth, depth: radius * 2 }
    };
  }

  // 生成复杂地形地图
  generateComplexTerrainMap(): MapData {
    const mapSize = 180;
    const obstacles: MapObstacle[] = [];
    const terrainFeatures: TerrainFeature[] = [];

    // 创建中央山丘
    const centralHill = this.createHill(0, 0, 15, 6);
    terrainFeatures.push(centralHill);

    // 创建四周的小山丘
    const hillPositions = [
      { x: -50, z: -50 },
      { x: 50, z: -50 },
      { x: -50, z: 50 },
      { x: 50, z: 50 }
    ];

    hillPositions.forEach(pos => {
      const hill = this.createHill(pos.x, pos.z, 10, 4);
      terrainFeatures.push(hill);
    });

    // 创建斜坡连接
    const ramp1 = this.createRamp(-25, 0, 10, 15, 3, 0);
    terrainFeatures.push(ramp1);
    const ramp2 = this.createRamp(25, 0, 10, 15, 3, Math.PI);
    terrainFeatures.push(ramp2);
    const ramp3 = this.createRamp(0, -25, 10, 15, 3, Math.PI / 2);
    terrainFeatures.push(ramp3);
    const ramp4 = this.createRamp(0, 25, 10, 15, 3, -Math.PI / 2);
    terrainFeatures.push(ramp4);

    // 创建高台
    const platforms = [
      { x: -40, z: 0, w: 12, d: 12, h: 5 },
      { x: 40, z: 0, w: 12, d: 12, h: 5 },
      { x: 0, z: -40, w: 12, d: 12, h: 4 },
      { x: 0, z: 40, w: 12, d: 12, h: 4 }
    ];

    platforms.forEach(p => {
      const platform = this.createPlatform(p.x, p.z, p.w, p.d, p.h);
      terrainFeatures.push(platform);
    });

    // 创建凹坑
    const pits = [
      { x: -30, z: -30, r: 6, d: 3 },
      { x: 30, z: -30, r: 6, d: 3 },
      { x: -30, z: 30, r: 6, d: 3 },
      { x: 30, z: 30, r: 6, d: 3 }
    ];

    pits.forEach(p => {
      const pit = this.createPit(p.x, p.z, p.r, p.d);
      terrainFeatures.push(pit);
    });

    // 添加一些障碍物
    for (let i = 0; i < 15; i++) {
      const angle = (i / 15) * Math.PI * 2;
      const radius = 60 + Math.random() * 30;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      const obstacle = this.createCrate(x, z);
      obstacles.push(obstacle);
    }

    return {
      name: '复杂地形战场',
      size: mapSize,
      obstacles,
      terrainFeatures,
      spawnPoints: [new THREE.Vector3(0, 6, 0)], // Spawn on central hill
      enemySpawnPoints: this.generateCircularSpawnPoints(70, 8)
    };
  }
}
